// React
import React, { useState, useEffect, Fragment, useContext } from 'react'
import PropTypes from 'prop-types'

// Components
import { DatePicker } from './DatePicker'
import PortalWrapper from 'ui/components/PortalWrapper'
import BPicker from 'ui/blocks/Picker'
import Popover from 'ui/components/Popover'
import Div from 'ui/elements/Div'
import { CalendarPageContext } from 'ui/components/Picker/Picker'

// Hooks
import { usePicker } from 'ui/components/Picker/hooks'

// Shared
import { Format as FormatUtils } from 'back_office/v2/utils/utils'

export const DatePickerButton = ({ paddingVertical = true, paddingHorizontal = true, ...props }) => {
  const [active, setActive] = useState(false)

  const picker = usePicker()

  const { setFocusedDate } = useContext(CalendarPageContext)

  useEffect(() => {
    if (!props.usePortal) return

    const isActive = picker.selection === props.mode

    setActive(isActive)

    props.onTrigger?.()

    if (isActive) {
      props.onOpen?.()
    } else {
      props.onHide?.()
    }
  }, [picker.selection, props.mode, props.usePortal])

  const value = props.field.value?.format?.(FormatUtils.dateFormat('date')) ?? props.placeholder

  const renderDayPicker = (popoverProps) => {
    const modifiers = [
      paddingVertical && 'padding-top-md padding-bottom-md',
      paddingHorizontal && 'padding-left-md padding-right-md'
    ].filter(Boolean).join(' ')

    return (
      <Div data-tid={`Date picker: ${props.mode}`} modifiers={modifiers}>
        <DatePicker {...props} {...popoverProps} />
      </Div>
    )
  }

  const handleClick = (e) => {
    e.preventDefault()

    if (active) {
      picker.setSelection(null)

      props.onHide?.()
    } else {
      picker.setSelection(props.mode)

      if (props.field.value) {
        setFocusedDate(props.field.value.toDate())
      }

      props.onOpen?.()
    }

    setActive((prev) => !prev)

    props.onTrigger?.()
  }

  const handleTrigger = () => {
    setActive(true)

    props.onTrigger?.()
  }

  const handleHide = () => {
    setActive(false)

    props.onHide?.()
  }

  const handleOpen = () => {
    setActive(true)

    picker.setSelection(props.mode)

    if (props.field.value) {
      setFocusedDate(props.field.value.toDate())
    }

    props.onOpen?.()
  }

  const handleSetActive = (e) => {
    e.preventDefault()

    setActive(true)
  }

  if (props.usePortal) {
    const isPortalOpen = props.isMobile ? active : active || picker.calendar === props.mode

    return (
      <Fragment>
        <BPicker.Button
          type="button"
          active={active}
          onClick={handleClick}
          empty={!props.field.value}
          disabled={props.disabled}
          data-value={props['data-value']}
          data-active={String(active)}
          error={props.error}
        >
          {value}
        </BPicker.Button>
        <PortalWrapper open={isPortalOpen} containerRef={props.portalRef}>
          {renderDayPicker({ handleClose: handleHide })}
        </PortalWrapper>
      </Fragment>
    )
  }

  return (
    <Popover
      {...(props.popoverOptions || {})}
      innerRef={props.innerRef}
      content={renderDayPicker}
      method="click"
      direction="bottom"
      offset={15}
      onTrigger={handleTrigger}
      onHide={handleHide}
      onOpen={handleOpen}
      delay={[200, null]}
    >
      <BPicker.Button
        type="button"
        active={active}
        empty={!props.field.value}
        disabled={props.disabled}
        data-value={props['data-value']}
        data-active={String(active)}
        onClick={handleSetActive}
        error={props.error}
      >
        {value}
      </BPicker.Button>
    </Popover>
  )
}

DatePickerButton.propTypes = {
  'field': PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date), PropTypes.object]),
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onInvalid: PropTypes.func,
    /** Custom validation function should return invalid key or true
     * ex. validate = (date) => 'HH'
     */
    validate: PropTypes.func
  }),
  'error': PropTypes.bool,
  'mode': PropTypes.string,
  'onSelectionChange': PropTypes.func,
  'popoverOptions': PropTypes.object,
  // placeholder can be a string or a React component
  'placeholder': PropTypes.any,
  'disabled': PropTypes.bool,
  'usePopover': PropTypes.bool,
  'innerRef': PropTypes.func,
  'onOpen': PropTypes.func,
  'onTrigger': PropTypes.func,
  'onHide': PropTypes.func,
  'format': PropTypes.string,
  'data-value': PropTypes.string,
  'usePortal': PropTypes.bool,
  'portalRef': PropTypes.any,
  'paddingVertical': PropTypes.bool,
  'paddingHorizontal': PropTypes.bool,
  'isMobile': PropTypes.bool
}
